import { useMemo, useState } from "react";
import type { EChartsOption } from "echarts";
import { Activity, Bot, DollarSign, Eye, Users } from "lucide-react";
import { formatNumber, useLocale, useTranslation } from "@ncp/i18n";
import { Avatar, Badge, Card, CardContent, CardDescription, CardHeader, CardTitle, EChart, EmptyState, NativeSelect, Progress, Skeleton, StatCard, chartPalette } from "@ncp/ui";
import { usePageUsage } from "../api";

const KINDS = ["open", "agent", "call", "job", "export", "improve", "error"] as const;
const usd = (n: number) => `$${n.toLocaleString(undefined, { maximumFractionDigits: n >= 100 ? 0 : 2 })}`;

/** How the page is used: opens and AI turns per day, who uses it and what it costs, what it calls and how often it is refused. */
export function UsageTab({ pageKey }: { pageKey: string }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const [days, setDays] = useState(30);
  const usage = usePageUsage(pageKey, days);
  const u = usage.data;

  const chart = useMemo<EChartsOption | null>(() => {
    if (!u) return null;
    const axis: string[] = [];
    for (let i = days - 1; i >= 0; i--) { const d = new Date(); d.setDate(d.getDate() - i); axis.push(d.toISOString().slice(0, 10)); }
    const series = KINDS.filter((k) => u.byDay.some((x) => x.kind === k)).map((k, i) => ({
      name: t(`admin:pageStudio.usage.kind.${k}`), type: "bar" as const, stack: "all",
      data: axis.map((day) => u.byDay.filter((x) => x.kind === k && x.day === day).reduce((n, x) => n + x.count, 0)),
      itemStyle: { color: k === "error" ? "#c0392b" : chartPalette[i % chartPalette.length] },
    }));
    return { grid: { left: 32, right: 12, top: 28, bottom: 24 }, legend: { top: 0, textStyle: { fontSize: 10 } }, tooltip: { trigger: "axis" },
      xAxis: { type: "category", data: axis.map((d) => d.slice(5)), axisLabel: { fontSize: 10 } }, yAxis: { type: "value", minInterval: 1, axisLabel: { fontSize: 10 } }, series };
  }, [u, days, t]);

  if (usage.isLoading) return <Skeleton className="h-80" />;
  if (!u) return null;
  const opens = u.byDay.filter((x) => x.kind === "open").reduce((n, x) => n + x.count, 0);
  const turns = u.agents.reduce((n, a) => n + a.calls, 0);
  const spend = u.agents.reduce((n, a) => n + a.costUsd, 0);
  const refused = u.calls.filter((c) => c.outcome === "denied" || c.outcome === "limited").reduce((n, c) => n + c.count, 0);
  const budgetPct = u.budget?.monthlyUsd ? Math.min(100, Math.round((u.budget.spentUsd / u.budget.monthlyUsd) * 100)) : null;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <NativeSelect value={String(days)} onChange={(e) => setDays(Number(e.target.value))} className="h-8 w-40" aria-label={t("admin:pageStudio.usage.window")}>
          {[7, 30, 90, 365].map((d) => <option key={d} value={d}>{t("admin:pageStudio.usage.days", { count: d })}</option>)}
        </NativeSelect>
      </div>
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard label={t("admin:pageStudio.usage.opens")} value={opens} icon={Eye} tone="gold" />
        <StatCard label={t("admin:pageStudio.usage.people")} value={u.users} icon={Users} tone="sea" />
        <StatCard label={t("admin:pageStudio.usage.turns")} value={turns} icon={Bot} tone="camel" footer={t("admin:pageStudio.usage.agentsUsed", { count: u.agents.length })} />
        <StatCard label={t("admin:pageStudio.usage.spend")} value={spend} format={usd} icon={DollarSign} tone="neutral" footer={u.budget?.monthlyUsd ? t("admin:pageStudio.usage.budget", { spent: usd(u.budget.spentUsd), budget: usd(u.budget.monthlyUsd) }) : t("admin:pageStudio.usage.noBudget")} />
      </div>
      {budgetPct !== null ? <Progress value={budgetPct} className="h-2" tone={budgetPct >= 90 ? "danger" : budgetPct >= 70 ? "warning" : "gold"} /> : null}

      <Card>
        <CardHeader><CardTitle className="flex items-center gap-2"><Activity className="size-4 text-aegold-700" /> {t("admin:pageStudio.usage.perDay")}</CardTitle></CardHeader>
        <CardContent>{chart && u.byDay.length ? <EChart option={chart} className="h-56" /> : <EmptyState title={t("admin:pageStudio.usage.none")} className="py-8" />}</CardContent>
      </Card>

      <div className="grid gap-4 xl:grid-cols-2">
        <Card>
          <CardHeader><CardTitle>{t("admin:pageStudio.usage.byUser")}</CardTitle><CardDescription>{t("admin:pageStudio.usage.byUserHint")}</CardDescription></CardHeader>
          <CardContent>
            {u.byUser.length === 0 ? <p className="text-sm text-muted-foreground">{t("admin:pageStudio.usage.none")}</p> : (
              <table className="w-full text-xs">
                <thead><tr className="border-b border-sand-200 text-muted-foreground"><th className="py-1 text-start font-medium">{t("admin:pageStudio.usage.person")}</th><th className="text-end font-medium">{t("admin:pageStudio.usage.opens")}</th><th className="text-end font-medium">{t("admin:pageStudio.usage.calls")}</th><th className="text-end font-medium">{t("admin:pageStudio.usage.turns")}</th><th className="text-end font-medium">{t("admin:pageStudio.usage.spend")}</th></tr></thead>
                <tbody>{u.byUser.map((p) => (
                  <tr key={p.userId} className="border-b border-sand-100">
                    <td className="py-1.5"><span className="flex items-center gap-2"><Avatar name={p.name ?? "?"} size="sm" />{p.name ?? "—"}</span></td>
                    <td className="text-end tabular">{formatNumber(p.opens, locale)}</td><td className="text-end tabular">{formatNumber(p.calls, locale)}</td><td className="text-end tabular">{formatNumber(p.turns, locale)}</td><td className="text-end tabular">{usd(p.costUsd)}</td>
                  </tr>
                ))}</tbody>
              </table>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">{t("admin:pageStudio.usage.capabilities")}{refused ? <Badge size="sm" variant="warning">{t("admin:pageStudio.usage.refused", { count: refused })}</Badge> : null}</CardTitle>
            <CardDescription>{t("admin:pageStudio.usage.capabilitiesHint")}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {u.calls.length === 0 ? <p className="text-sm text-muted-foreground">{t("admin:pageStudio.usage.none")}</p> : (
              <ul className="space-y-1 text-xs">
                {u.calls.map((c) => (
                  <li key={`${c.capability}:${c.outcome}`} className="flex items-center gap-2">
                    <code className="min-w-0 flex-1 truncate font-mono" dir="ltr">{c.capability}</code>
                    {c.outcome !== "ok" ? <Badge size="sm" variant="warning">{t(`admin:pageStudio.usage.outcome.${c.outcome}`, { defaultValue: c.outcome })}</Badge> : null}
                    <span className="tabular text-muted-foreground">{formatNumber(c.count, locale)} · {formatNumber(c.avgMs, locale)} ms</span>
                  </li>
                ))}
              </ul>
            )}
            {u.agents.length ? (
              <div>
                <p className="mb-1 text-xs font-semibold">{t("admin:pageStudio.usage.agents")}</p>
                <ul className="space-y-1 text-xs">{u.agents.map((a) => <li key={a.agent} className="flex justify-between"><code dir="ltr">{a.agent}</code><span className="tabular text-muted-foreground">{formatNumber(a.calls, locale)} · {usd(a.costUsd)}</span></li>)}</ul>
              </div>
            ) : null}
            {u.bySpace.length ? (
              <div>
                <p className="mb-1 text-xs font-semibold">{t("admin:pageStudio.usage.bySpace")}</p>
                <div className="flex flex-wrap gap-1.5">{u.bySpace.map((s) => <Badge key={s.space ?? "-"} variant="outline" size="sm">{s.space ?? "—"} · {formatNumber(s.opens, locale)}</Badge>)}</div>
              </div>
            ) : null}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
